/*
 * The app's colour tokens (docs/26 §2): the solid colours a theme sets, by the CSS variable names the customer app
 * already uses (apps/customer/src/prototype.css), with today's values as the starting look (Prototype D). The app's
 * see-through colours (hairlines, chips, scrims, soft fills) are worked out from these (DERIVED_TOKENS), so every
 * stored colour is solid and its contrast can be measured.
 */

export type ThemeTokenGroup = 'brand' | 'surfaces' | 'text' | 'status';

export interface ThemeTokenDef {
  /** The CSS variable, without "--". */
  key: string;
  group: ThemeTokenGroup;
  label: string;
  /** Where it shows, in words. */
  sub: string;
  light: string;
  dark: string;
}

export const THEME_TOKENS = [
  { key: 'yellow', group: 'brand', label: 'Brand colour', sub: 'Main buttons, the Card tab and highlights', light: '#ffd84d', dark: '#ffd84d' },
  { key: 'on-yellow', group: 'brand', label: 'Text on the brand colour', sub: 'Words and icons on main buttons', light: '#141210', dark: '#141210' },
  { key: 'accent-text', group: 'brand', label: 'Brand text', sub: 'Links and words in the brand colour', light: '#7a5500', dark: '#ffd84d' },
  { key: 'accent-soft', group: 'brand', label: 'Soft brand fill', sub: 'Badges and gentle highlights', light: '#fff5cc', dark: '#2a2108' },
  { key: 'sel', group: 'brand', label: 'Selected', sub: 'Switches that are on and chosen chips', light: '#121110', dark: '#f1ede4' },
  { key: 'bg', group: 'surfaces', label: 'Page background', sub: 'Behind everything on a phone', light: '#f6f4ef', dark: '#0f0f0e' },
  { key: 'page', group: 'surfaces', label: 'Laptop margin', sub: 'Either side of the app on a laptop', light: '#e9e7e2', dark: '#000000' },
  { key: 'surface', group: 'surfaces', label: 'Cards', sub: 'Cards, sheets and lists', light: '#ffffff', dark: '#1b1a19' },
  { key: 'bar', group: 'surfaces', label: 'Tab bar and header', sub: 'The bars at the top and bottom', light: '#ffffff', dark: '#151514' },
  { key: 'fill', group: 'surfaces', label: 'Buttons and fields', sub: 'Second buttons, inputs and pills', light: '#eeebe5', dark: '#2a2927' },
  { key: 'tile-soft', group: 'surfaces', label: 'Soft tiles', sub: 'Menu tiles and quiet panels', light: '#f3f1ec', dark: '#242321' },
  { key: 'ink', group: 'text', label: 'Text', sub: 'Headings and body text', light: '#121110', dark: '#f6f5f2' },
  { key: 'soft', group: 'text', label: 'Secondary text', sub: 'Descriptions and second lines', light: '#34322f', dark: '#dad7d1' },
  { key: 'muted', group: 'text', label: 'Quiet text', sub: 'Hints, times and tab names not chosen', light: '#625e57', dark: '#aaa69f' },
  { key: 'good', group: 'status', label: 'Done and on', sub: 'Ready, collected, points added', light: '#1f7a3a', dark: '#6fd08c' },
  { key: 'violet', group: 'status', label: 'Founders and specials', sub: 'The Founders Club badge and secret menu', light: '#6d28d9', dark: '#c4b5fd' },
  { key: 'danger', group: 'status', label: 'Delete button', sub: 'Delete account and other “can’t undo” buttons', light: '#c62828', dark: '#c62828' },
  { key: 'on-danger', group: 'status', label: 'Text on the delete button', sub: 'Words on that button', light: '#ffffff', dark: '#ffffff' },
  { key: 'danger-text', group: 'status', label: 'Problem text', sub: 'Errors and warnings in words', light: '#c62828', dark: '#ff8a80' },
] as const satisfies readonly ThemeTokenDef[];

export type ThemeToken = (typeof THEME_TOKENS)[number]['key'];
export const THEME_TOKEN_KEYS = THEME_TOKENS.map((t) => t.key) as ThemeToken[];
export const THEME_GROUPS: { id: ThemeTokenGroup; label: string }[] = [
  { id: 'brand', label: 'Brand' }, { id: 'surfaces', label: 'Backgrounds' }, { id: 'text', label: 'Text' }, { id: 'status', label: 'Status' },
];

/** Text and buttons that must keep 4.5:1, in light and in dark (CLAUDE.md §4 guard rails). */
export interface ContrastPair { id: string; label: string; fg: ThemeToken; bg: ThemeToken }
export const CONTRAST_PAIRS: readonly ContrastPair[] = [
  { id: 'ink-on-bg', label: 'Text on the page background', fg: 'ink', bg: 'bg' },
  { id: 'ink-on-surface', label: 'Text on cards', fg: 'ink', bg: 'surface' },
  { id: 'ink-on-bar', label: 'Text on the tab bar', fg: 'ink', bg: 'bar' },
  { id: 'ink-on-fill', label: 'Text on second buttons', fg: 'ink', bg: 'fill' },
  { id: 'ink-on-tile', label: 'Text on soft tiles', fg: 'ink', bg: 'tile-soft' },
  { id: 'soft-on-bg', label: 'Secondary text on the page background', fg: 'soft', bg: 'bg' },
  { id: 'soft-on-surface', label: 'Secondary text on cards', fg: 'soft', bg: 'surface' },
  { id: 'muted-on-bg', label: 'Quiet text on the page background', fg: 'muted', bg: 'bg' },
  { id: 'muted-on-surface', label: 'Quiet text on cards', fg: 'muted', bg: 'surface' },
  { id: 'muted-on-bar', label: 'Tab names on the tab bar', fg: 'muted', bg: 'bar' },
  { id: 'on-yellow', label: 'Text on main buttons', fg: 'on-yellow', bg: 'yellow' },
  { id: 'accent-on-bg', label: 'Brand text on the page background', fg: 'accent-text', bg: 'bg' },
  { id: 'accent-on-surface', label: 'Brand text on cards', fg: 'accent-text', bg: 'surface' },
  { id: 'accent-on-soft', label: 'Badge text on the soft brand fill', fg: 'accent-text', bg: 'accent-soft' },
  { id: 'bg-on-sel', label: 'Text on a chosen chip', fg: 'bg', bg: 'sel' },
  { id: 'good-on-surface', label: 'Green status text on cards', fg: 'good', bg: 'surface' },
  { id: 'violet-on-surface', label: 'Founders text on cards', fg: 'violet', bg: 'surface' },
  { id: 'on-danger', label: 'Text on the delete button', fg: 'on-danger', bg: 'danger' },
  { id: 'danger-on-surface', label: 'Problem text on cards', fg: 'danger-text', bg: 'surface' },
  { id: 'danger-on-bg', label: 'Problem text on the page background', fg: 'danger-text', bg: 'bg' },
];

/** The Wallet pass's text on its background (Apple's foreground and label colours; Google uses the background). */
export const PASS_PAIRS: readonly { id: string; label: string; fg: 'foreground' | 'label'; bg: 'background' }[] = [
  { id: 'pass-text', label: 'Pass text on the pass', fg: 'foreground', bg: 'background' },
  { id: 'pass-label', label: 'Pass labels on the pass', fg: 'label', bg: 'background' },
];

/**
 * The see-through colours the app works out from the stored ones (CSS color-mix in sRGB), so nothing on screen is a
 * colour the theme can't change. `mix(a, n%, b)` is color-mix(in srgb, var(--a) n%, b).
 */
export const DERIVED_TOKENS: readonly { key: string; css: string }[] = [
  { key: 'line', css: 'color-mix(in srgb, var(--ink) 10%, transparent)' },
  { key: 'line2', css: 'color-mix(in srgb, var(--ink) 18%, transparent)' },
  { key: 'edge', css: 'color-mix(in srgb, var(--ink) 13%, transparent)' },
  { key: 'accent-line', css: 'var(--ink)' },
  { key: 'on-yellow-soft', css: 'color-mix(in srgb, var(--on-yellow) 80%, var(--yellow))' },
  { key: 'chip', css: 'color-mix(in srgb, var(--surface) 95%, transparent)' },
  { key: 'fx-bg', css: 'color-mix(in srgb, var(--surface) 97%, transparent)' },
  { key: 'good-soft', css: 'color-mix(in srgb, var(--good) 12%, transparent)' },
  { key: 'violet-soft', css: 'color-mix(in srgb, var(--violet) 11%, transparent)' },
  { key: 'spin-glow', css: 'color-mix(in srgb, var(--yellow) 55%, transparent)' },
  { key: 'spin-ray', css: 'color-mix(in srgb, var(--yellow) 30%, transparent)' },
  { key: 'disabled-bg', css: 'var(--fill)' },
  { key: 'disabled-ink', css: 'color-mix(in srgb, var(--muted) 75%, var(--fill))' },
];

/**
 * The glass (Saif, 21 Oct 2026): the tab bar, the floating Show code's backdrop, sheets and sticky headers are the bar
 * colour at this opacity over a strong blur, so the glow, grain and scrolling content show through. The guard rail: whatever
 * scrolls behind (the yellow card, a photo, black or white), text on glass (always the ink colour; tab names included) keeps
 * 4.5:1 against the glass itself, which the appearance tests check at the extremes.
 *
 * Saif, 21 Oct evening, after build 2610211752 ("you haven't really done the frosted glass"): at 62% plus two white inner
 * highlights the layer came out around 85% white, which over a cream page is simply white. Half is the clearest the light
 * layer can be with dark text over pure black behind it (4.6:1). Dark glass darkens what is behind it first (the backdrop
 * filter's brightness, GLASS_DIM_DARK), which is what lets a dark layer go to half as well while pale text stays readable
 * over white content. The CSS (apps/customer styles.css, --glass-a / --glass-a-dark / --glass-dim-dark) must carry the same
 * numbers; a customer test checks it.
 */
export const GLASS_ALPHA = 0.5;
export const GLASS_ALPHA_DARK = 0.5;
/** Dark mode: the glass dims what is behind it to this fraction before compositing (backdrop-filter brightness). */
export const GLASS_DIM_DARK = 0.6;

/** The colour the eye sees: `fg` at `alpha` over `behind` (#rrggbb each). */
export function composite(fg: string, behind: string, alpha: number): string {
  const c = (h: string, i: number) => Number.parseInt(h.slice(1 + i * 2, 3 + i * 2), 16);
  const mix = (i: number) => Math.round(c(fg, i) * alpha + c(behind, i) * (1 - alpha));
  return `#${[0, 1, 2].map((i) => mix(i).toString(16).padStart(2, '0')).join('')}`;
}
